import React, { useState } from 'react';
import { ArrowRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { trackFormSubmit, trackConversion } from '../utils/analytics';

const ContactModal = ({ isOpen, onClose, title = "Get Your Free Strategy" }) => {
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    niche: '',
    objective: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success("Success!", {
          description: data.message,
        });
        
        // Track successful form submission and conversion
        trackFormSubmit('popup_contact_form', formData);
        trackConversion('popup_lead_generation', 100);
        
        // Reset form and close modal
        setFormData({
          name: '',
          contact: '',
          niche: '',
          objective: ''
        });
        onClose();
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (error) {
      toast.error("Error", {
        description: error.message || "Something went wrong. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          className="relative w-full max-w-2xl transform transition-all"
          style={{ 
            backgroundColor: 'rgba(48, 47, 44, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)'
          }}
          className="rounded-2xl border p-8"
          style={{ borderColor: 'rgba(63, 72, 22, 0.3)' }}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full transition-colors hover:bg-opacity-20"
            style={{ backgroundColor: 'rgba(217, 251, 6, 0.1)' }}
          >
            <X className="h-5 w-5" style={{ color: 'var(--text-primary)' }} />
          </button>
          
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="heading-2 mb-4" style={{ color: 'var(--text-primary)' }}>
              {title}
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Get a personalized advertising strategy tailored to your business goals.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="modal-name" className="block body-small mb-2" style={{ color: 'var(--text-primary)' }}>
                  Name *
                </label>
                <input
                  id="modal-name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full p-4 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 focus:scale-105"
                  style={{ 
                    backgroundColor: 'rgba(26, 28, 27, 0.8)',
                    borderColor: 'var(--border-medium)',
                    color: 'var(--text-primary)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}
                  placeholder="Your full name"
                />
              </div>
              
              <div>
                <label htmlFor="modal-contact" className="block body-small mb-2" style={{ color: 'var(--text-primary)' }}>
                  Email or Telegram *
                </label>
                <input
                  id="modal-contact"
                  name="contact"
                  type="text"
                  required
                  value={formData.contact}
                  onChange={handleInputChange}
                  className="w-full p-4 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 focus:scale-105"
                  style={{ 
                    backgroundColor: 'rgba(26, 28, 27, 0.8)',
                    borderColor: 'var(--border-medium)',
                    color: 'var(--text-primary)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}
                  placeholder="email@example.com or @telegram"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="modal-niche" className="block body-small mb-2" style={{ color: 'var(--text-primary)' }}>
                  Niche *
                </label>
                <input
                  id="modal-niche"
                  name="niche"
                  type="text"
                  required
                  value={formData.niche}
                  onChange={handleInputChange}
                  className="w-full p-4 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 focus:scale-105"
                  style={{ 
                    backgroundColor: 'rgba(26, 28, 27, 0.8)',
                    borderColor: 'var(--border-medium)',
                    color: 'var(--text-primary)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}
                  placeholder="E-commerce, SaaS, Services..."
                />
              </div>
              
              <div>
                <label htmlFor="modal-objective" className="block body-small mb-2" style={{ color: 'var(--text-primary)' }}>
                  Advertising Objective *
                </label>
                <input
                  id="modal-objective"
                  name="objective"
                  type="text"
                  required
                  value={formData.objective}
                  onChange={handleInputChange}
                  className="w-full p-4 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 focus:scale-105"
                  style={{ 
                    backgroundColor: 'rgba(26, 28, 27, 0.8)',
                    borderColor: 'var(--border-medium)',
                    color: 'var(--text-primary)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}
                  placeholder="Increase sales, generate leads..."
                />
              </div>
            </div>

            <div className="text-center pt-4">
              <button 
                type="submit" 
                disabled={isSubmitting}
                className="btn-primary btn-animated px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 inline-flex items-center pulse-glow"
                style={{ 
                  backgroundColor: 'var(--brand-primary)',
                  color: 'var(--text-inverse)'
                }}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <ArrowRight className="mr-2 h-5 w-5" />
                    Get My Free Strategy
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;